import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Brain, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, RefreshCw,
  BarChart3, Target, Shield, Lightbulb, Clock, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIInsightsPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [summary, setSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [metricsRes, trendsRes, forecastRes, deptRes] = await Promise.all([
        axios.get(`${API_URL}/api/ai-insights/metrics?days=7`, { headers }),
        axios.get(`${API_URL}/api/ai-insights/trends?days=14`, { headers }),
        axios.get(`${API_URL}/api/ai-insights/risk-forecast`, { headers }),
        axios.get(`${API_URL}/api/ai-insights/department-comparison`, { headers })
      ]);

      setMetrics(metricsRes.data);
      setTrends(trendsRes.data.trends);
      setForecast(forecastRes.data);
      setDepartments(deptRes.data.departments);

      // Try to get cached summary
      try {
        const summaryRes = await axios.get(`${API_URL}/api/ai-insights/weekly-summary`, { headers });
        setSummary(summaryRes.data);
      } catch (e) {
        // No cached summary available
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load insights", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const generateNewSummary = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/ai-insights/generate-summary?days=7`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
      toast({ title: "Success", description: "AI summary generated!" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate summary", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-orange-600 bg-orange-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Brain className="w-7 h-7 text-purple-500" />
            AI Safety Insights
          </h1>
          <p className="text-slate-600 mt-1">
            AI-powered analytics, trends, and predictive safety insights
          </p>
        </div>
        <Button
          onClick={generateNewSummary}
          disabled={generating}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          data-testid="generate-summary-btn"
        >
          {generating ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Brain className="w-4 h-4 mr-2" />
          )}
          Generate AI Summary
        </Button>
      </div>

      {/* Risk Forecast Card */}
      {forecast && (
        <Card className={`border-2 ${
          forecast.risk_level === 'high' ? 'border-red-200 bg-red-50/50' :
          forecast.risk_level === 'medium' ? 'border-orange-200 bg-orange-50/50' :
          forecast.risk_level === 'low' ? 'border-green-200 bg-green-50/50' :
          'border-blue-200 bg-blue-50/50'
        }`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-4 rounded-full ${getRiskColor(forecast.risk_level)}`}>
                  <Target className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Risk Forecast</p>
                  <p className={`text-3xl font-bold capitalize ${
                    forecast.risk_level === 'high' ? 'text-red-600' :
                    forecast.risk_level === 'medium' ? 'text-orange-600' :
                    forecast.risk_level === 'low' ? 'text-green-600' :
                    'text-blue-600'
                  }`}>
                    {forecast.risk_level} Risk
                  </p>
                  <p className="text-sm text-slate-600 mt-1">{forecast.forecast}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Risk Score</p>
                <p className={`text-4xl font-bold ${getScoreColor(100 - forecast.risk_score)}`}>
                  {forecast.risk_score}
                </p>
              </div>
            </div>
            
            {/* Trend Indicators */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="p-3 bg-white rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Incidents</span>
                  <Badge variant={forecast.incident_trend.change_percent > 0 ? "destructive" : "success"}>
                    {forecast.incident_trend.change_percent > 0 ? (
                      <ArrowUpRight className="w-3 h-3 mr-1" />
                    ) : (
                      <ArrowDownRight className="w-3 h-3 mr-1" />
                    )}
                    {Math.abs(forecast.incident_trend.change_percent)}%
                  </Badge>
                </div>
                <p className="text-lg font-semibold mt-1">
                  {forecast.incident_trend.current_week} this week
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Alerts</span>
                  <Badge variant={forecast.alert_trend.change_percent > 0 ? "destructive" : "success"}>
                    {forecast.alert_trend.change_percent > 0 ? (
                      <ArrowUpRight className="w-3 h-3 mr-1" />
                    ) : (
                      <ArrowDownRight className="w-3 h-3 mr-1" />
                    )}
                    {Math.abs(forecast.alert_trend.change_percent)}%
                  </Badge>
                </div>
                <p className="text-lg font-semibold mt-1">
                  {forecast.alert_trend.current_week} this week
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metrics Grid */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Incidents (7d)</p>
                  <p className="text-2xl font-bold text-slate-900">{metrics.incidents.total}</p>
                  <p className="text-xs text-red-600">{metrics.incidents.critical} critical</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Hazard Scans</p>
                  <p className="text-2xl font-bold text-slate-900">{metrics.hazard_scans.total}</p>
                  <p className="text-xs text-orange-600">{metrics.hazard_scans.hazard_rate}% with hazards</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <BarChart3 className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">CCTV Alerts</p>
                  <p className="text-2xl font-bold text-slate-900">{metrics.cctv_alerts.total}</p>
                  <p className="text-xs text-green-600">{metrics.cctv_alerts.resolution_rate}% resolved</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Training Completed</p>
                  <p className="text-2xl font-bold text-slate-900">{metrics.training_completed}</p>
                  <p className="text-xs text-slate-500">{metrics.audits_completed} audits</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Summary Section */}
      {summary?.ai_summary && (
        <Card className="border-purple-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                <CardTitle>AI Weekly Summary</CardTitle>
              </div>
              {summary.ai_summary.safety_score && (
                <div className="text-right">
                  <p className="text-xs text-slate-500">Safety Score</p>
                  <p className={`text-3xl font-bold ${getScoreColor(summary.ai_summary.safety_score)}`}>
                    {summary.ai_summary.safety_score}
                  </p>
                </div>
              )}
            </div>
            <CardDescription className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Generated {new Date(summary.generated_at).toLocaleString()}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Executive Summary */}
            {summary.ai_summary.executive_summary && (
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="font-medium text-purple-900">Executive Summary</p>
                <p className="text-purple-800 mt-2">{summary.ai_summary.executive_summary}</p>
              </div>
            )}

            {/* Key Findings */}
            {summary.ai_summary.key_findings?.length > 0 && (
              <div>
                <p className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  Key Findings
                </p>
                <ul className="space-y-2">
                  {summary.ai_summary.key_findings.map((finding, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      {finding}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Areas of Concern */}
              {summary.ai_summary.areas_of_concern?.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="font-medium text-red-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Areas of Concern
                  </p>
                  <ul className="space-y-1">
                    {summary.ai_summary.areas_of_concern.map((concern, i) => (
                      <li key={i} className="text-sm text-red-800 flex items-start gap-2">
                        <span className="text-red-500">•</span>
                        {concern}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Positive Trends */}
              {summary.ai_summary.positive_trends?.length > 0 && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="font-medium text-green-900 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Positive Trends
                  </p>
                  <ul className="space-y-1">
                    {summary.ai_summary.positive_trends.map((trend, i) => (
                      <li key={i} className="text-sm text-green-800 flex items-start gap-2">
                        <span className="text-green-500">•</span>
                        {trend}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Recommendations */}
            {summary.ai_summary.recommendations?.length > 0 && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="font-medium text-blue-900 mb-2">Recommendations</p>
                <ol className="space-y-2">
                  {summary.ai_summary.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-3 text-blue-800">
                      <span className="w-6 h-6 bg-blue-200 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                        {i + 1}
                      </span>
                      {rec}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Trend Chart */}
      {trends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              14-Day Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-end gap-1">
              {trends.map((day, i) => {
                const maxVal = Math.max(...trends.map(t => t.incidents + t.alerts + t.scans), 1);
                const height = ((day.incidents + day.alerts + day.scans) / maxVal) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full bg-gradient-to-t from-blue-500 to-purple-500 rounded-t transition-all hover:from-blue-600 hover:to-purple-600"
                      style={{ height: `${Math.max(height, 5)}%` }}
                      title={`${day.date}: ${day.incidents} incidents, ${day.alerts} alerts, ${day.scans} scans`}
                    ></div>
                    {i % 2 === 0 && (
                      <span className="text-xs text-slate-400 transform -rotate-45 origin-left">
                        {day.date.slice(5)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <span className="w-3 h-3 bg-blue-500 rounded"></span> Combined Activity
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Department Comparison */}
      {departments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Department Safety Scores</CardTitle>
            <CardDescription>30-day performance comparison</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {departments.map((dept, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-32 text-sm font-medium text-slate-700 truncate">
                    {dept.department}
                  </div>
                  <div className="flex-1">
                    <div className="h-6 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          dept.safety_score >= 80 ? 'bg-green-500' :
                          dept.safety_score >= 60 ? 'bg-blue-500' :
                          dept.safety_score >= 40 ? 'bg-orange-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${dept.safety_score}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-20 text-right">
                    <span className={`font-bold ${getScoreColor(dept.safety_score)}`}>
                      {dept.safety_score}
                    </span>
                    <span className="text-xs text-slate-400 ml-1">/ 100</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
